import React, { memo } from 'react';
import { Box, Typography, Chip, Divider } from '@mui/material';
import { AccessTime, Label } from '@mui/icons-material';
import Thumbnail from '../Timevents/Thumbnail';

// Theme colors to match Calendar component
const primaryColor = '#5757b0';
const lightPurple = '#f0f0ff';

function TooltipContent({ event }) {
	// Returns tooltip content for a given event.
	const { title, customFields, date, eventType } = event;

	return (
		<Box 
			sx={{ 
				display: 'flex', 
				alignItems: 'flex-start', 
				p: 2,
				minWidth: 260,
				maxWidth: 380,
				backgroundColor: 'rgba(255, 255, 255, 0.95)',
				backdropFilter: 'blur(10px)',
				borderRadius: '12px',
				border: `1px solid ${lightPurple}`,
				boxShadow: '0 12px 28px rgba(87, 87, 176, 0.15)',
				position: 'relative',
				overflow: 'hidden',
				transition: 'all 0.3s ease',
			}}
		>
			{/* Thumbnail container with consistent styling */}
			<Box 
				sx={{ 
					flexShrink: 0,
					borderRadius: '8px',
					overflow: 'hidden',
					border: `1px solid ${lightPurple}`,
					boxShadow: '0 2px 8px rgba(87, 87, 176, 0.1)',
					backgroundColor: 'white'
				}}
			>
				<Thumbnail timeevent={event} />
			</Box>

			{/* Content container */}
			<Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
				{/* Event type chip - matching application style */}
				{eventType && (
					<Chip
						icon={<Label sx={{ fontSize: '12px !important' }} />}
						label={eventType}
						size="small"
						sx={{
							mb: 1,
							backgroundColor: lightPurple,
							color: primaryColor,
							border: `1px solid ${primaryColor}40`,
							fontWeight: 600,
							fontSize: '0.65rem',
							height: '22px',
							borderRadius: '8px',
							'& .MuiChip-icon': {
								color: primaryColor,
							}
						}}
					/>
				)}

				{/* Title with application typography */}
				<Typography 
					variant="body2" 
					sx={{ 
						fontWeight: 700,
						color: '#2c2c2c',
						fontSize: '0.85rem',
						lineHeight: 1.4,
						mb: 1.5,
						display: '-webkit-box',
						WebkitLineClamp: 2,
						WebkitBoxOrient: 'vertical',
						overflow: 'hidden'
					}}
				>
					{title}
				</Typography>

				{/* Date with icon - matching app style */}
				<Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
					<AccessTime 
						sx={{ 
							fontSize: '14px', 
							color: primaryColor,
							mr: 0.5 
						}} 
					/>
					<Typography 
						variant="caption" 
						sx={{ 
							color: '#666',
							fontWeight: 500,
							fontSize: '0.7rem'
						}}
					>
						{date}
					</Typography>
				</Box>

				{/* Custom fields section with app styling */}
				{customFields?.length > 0 && (
					<>
						<Divider 
							sx={{ 
								my: 1.5,
								borderColor: 'rgba(87, 87, 176, 0.15)'
							}} 
						/>
						<Box sx={{ mt: 1 }}>
							<Typography 
								variant="caption" 
								sx={{ 
									fontWeight: 700,
									color: primaryColor,
									fontSize: '0.65rem',
									textTransform: 'uppercase',
									letterSpacing: '0.5px',
									mb: 0.5,
									display: 'block'
								}}
							>
								Tags
							</Typography>
							{customFields.map((cf, index) => (
								<Box
									key={index}
									sx={{
										display: 'flex',
										alignItems: 'flex-start',
										mb: 0.5,
										p: 0.75,
										borderRadius: '8px',
										backgroundColor: cf.reply 
											? 'rgba(87, 87, 176, 0.04)' 
											: 'rgba(0, 0, 0, 0.02)',
										border: `1px solid ${cf.reply 
											? 'rgba(87, 87, 176, 0.15)' 
											: 'rgba(0, 0, 0, 0.05)'}`,
										transition: 'all 0.2s ease',
									}}
								>
									<Typography
										variant="caption"
										sx={{
											fontSize: '0.65rem',
											lineHeight: 1.4,
											color: '#333'
										}}
									>
										<Box 
											component="span" 
											sx={{ 
												fontWeight: 700,
												color: primaryColor
											}}
										>
											{cf.variable}:
										</Box>
										{' '}
										<Box 
											component="span" 
											sx={{ 
												color: cf.reply ? '#333' : '#999',
												fontStyle: cf.reply ? 'normal' : 'italic'
											}}
										>
											{cf.reply 
												? (Array.isArray(cf.reply) 
													? JSON.stringify(cf.reply, null, 2)
													: cf.reply.toString())
												: 'No value'}
										</Box>
									</Typography>
								</Box>
							))}
						</Box>
					</>
				)}
			</Box>
		</Box>
	);
}

export default memo(TooltipContent);
